/**
 * Card cache state - Jotai atom for caching full ScryfallCard data
 *
 * Stores full card data fetched from Firestore to avoid repeated fetches.
 * The cache is populated during app initialization and updated incrementally
 * when new cards are added to the collection.
 */
import { atom } from 'jotai';

import type { ScryfallCard } from '@/types/card';

/** Type alias for the card cache map */
export type ScryfallCardCache = Map<string, ScryfallCard>;

/**
 * Atom storing the full Scryfall card cache
 * Key: Scryfall ID (string)
 * Value: Full ScryfallCard object
 */
export const fullCardCacheAtom = atom<ScryfallCardCache>(new Map());

/**
 * Derived atom to get a card from cache by ID
 * Returns null if not in cache
 */
export const getCardFromCacheAtom = atom((get) => {
  const cache = get(fullCardCacheAtom);
  return (id: string): ScryfallCard | null => cache.get(id) ?? null;
});

/**
 * Derived atom to check if cards are in cache
 * Returns array of IDs that are NOT in cache
 */
export const getMissingCardIdsAtom = atom((get) => {
  const cache = get(fullCardCacheAtom);
  return (ids: string[]): string[] => ids.filter((id) => !cache.has(id));
});

/**
 * Derived atom for cache stats
 */
export const cardCacheStatsAtom = atom((get) => {
  const cache = get(fullCardCacheAtom);
  return {
    size: cache.size,
    isEmpty: cache.size === 0,
  };
});
